import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchDesignationsList, updateDesignation } from './services/api';
import { Designation } from './types';
import { Button, Form, Card, Alert, Container, Spinner } from 'react-bootstrap';

const userTypes = [
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'SUPERVISOR', label: 'Supervisor' },
  { value: 'USER', label: 'User' },
];

const EditDesignation: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [designation, setDesignation] = useState<Designation | null>(null);
  const [name, setName] = useState('');
  const [userType, setUserType] = useState('ADMIN');
  const [remoteCheckin, setRemoteCheckin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchDesignationsList();
        const designations = Array.isArray(response.data) ? response.data : [];
        
        const found = designations.find(d => d.id === Number(id));
        if (!found) throw new Error('Designation not found');
        
        setDesignation(found);
        setName(found.name);
        setUserType(found.user_type);
        setRemoteCheckin(found.remote_checkin);
      } catch (error) {
        console.error('Error loading designation:', error);
        setError('Failed to load designation');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateDesignation(Number(id), { name, user_type: userType, remote_checkin: remoteCheckin });
      setSuccess(true);
      setTimeout(() => navigate('/designations'), 1200);
    } catch (error) {
      console.error('Error updating designation:', error);
      setError('Failed to update designation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!designation) {
    return (
      <Container className="py-4">
        <Alert variant="warning">Designation not found.</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: 500 }}>
      <Card className="shadow-sm">
        <Card.Body>
          <h2 className="mb-4">Edit Designation</h2>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                maxLength={50}
                placeholder="Enter designation name"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>User Type</Form.Label>
              <Form.Select
                value={userType}
                onChange={e => setUserType(e.target.value)}
                required
              >
                {userTypes.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                id="remoteCheckin"
                label="Remote Check-in"
                checked={remoteCheckin}
                onChange={e => setRemoteCheckin(e.target.checked)}
              />
            </Form.Group>
            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
            {success && <Alert variant="success" className="mb-3">Designation updated!</Alert>}
            <div className="d-flex justify-content-between">
              <Button variant="secondary" onClick={() => navigate('/designations')} disabled={saving}>
                Back to List
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EditDesignation; 